/**
 * System Settings Tab
 *
 * Manage evaluation settings and feature flags.
 */

import { useState, useEffect } from 'react';
import { useEvaluationSettings, useFeatureFlags, useUpdateSystemSettings, useSystemSettings } from '../useSettings';
import {
  SYSTEM_SETTING_KEYS,
  DEFAULT_VAKANTIEDAGEN_PER_JAAR,
  DEFAULT_PAUZE_VENSTER,
  parsePauzeVenster,
  serializePauzeVenster,
  pauzeVensterSchema,
} from '@functieloonhuis/shared';
import type { UpdateSystemSettingsRequest, PauzeVenster } from '@functieloonhuis/shared';

interface EvalSettings {
  task_weight: number;
  vak_competency_weight: number;
  basis_competency_weight: number;
  score_scale_min: number;
  score_scale_max: number;
  auto_save_interval: number;
  require_self_evaluation: boolean;
}

interface Features {
  self_evaluation: boolean;
  conversation_mode: boolean;
  pdf_export: boolean;
  salary_display: boolean;
}

export function SystemSettingsTab() {
  const { data: evalSettings, isLoading: evalLoading } = useEvaluationSettings();
  const { data: featureFlags, isLoading: featuresLoading } = useFeatureFlags();
  // Verlofinstellingen via de GENERIEKE lezer — geen apart endpoint nodig voor
  // één getal, en zo blijft de sleutel op één plek gedefinieerd (shared).
  const { data: verlofSettings, isLoading: verlofLoading } = useSystemSettings(
    SYSTEM_SETTING_KEYS.verlof.category
  );
  const updateSettings = useUpdateSystemSettings();

  const [vakantiedagenPerJaar, setVakantiedagenPerJaar] = useState<number>(
    DEFAULT_VAKANTIEDAGEN_PER_JAAR
  );

  // Epic 1 — Verlof correctie/kort verlof, Story 1.1. Zelfde generieke lezer
  // als vakantiedagenPerJaar hierboven; geen tweede aanroep nodig.
  const [pauzeVenster, setPauzeVenster] = useState<PauzeVenster>(
    parsePauzeVenster(DEFAULT_PAUZE_VENSTER)!
  );

  /**
   * Story 5.4 (Verlofsoorten, AC-10): de dag vanaf wanneer de nieuwe
   * verlofsoorten gelden.
   *
   * Begint LEEG, en dat is het punt van de story: anders dan de twee
   * instellingen hierboven is er hier geen `DEFAULT_`-waarde. Een
   * voorgevulde datum zou het hele verlofsoorten-werk stilzwijgend aanzetten
   * op een omgeving zonder testvangnet (KE-8). Leeg betekent: de nieuwe
   * regels gelden niet.
   */
  const [verlofsoortIngangsdatum, setVerlofsoortIngangsdatum] = useState<string>('');

  const [evalForm, setEvalForm] = useState<EvalSettings>({
    task_weight: 50,
    vak_competency_weight: 30,
    basis_competency_weight: 20,
    score_scale_min: 1,
    score_scale_max: 5,
    auto_save_interval: 30,
    require_self_evaluation: true,
  });

  const [featuresForm, setFeaturesForm] = useState<Features>({
    self_evaluation: true,
    conversation_mode: true,
    pdf_export: true,
    salary_display: false,
  });

  const [isDirty, setIsDirty] = useState(false);

  // Sync form with loaded data
  useEffect(() => {
    if (evalSettings) {
      setEvalForm(evalSettings);
    }
  }, [evalSettings]);

  useEffect(() => {
    if (featureFlags) {
      setFeaturesForm(featureFlags);
    }
  }, [featureFlags]);

  useEffect(() => {
    // De generieke lezer geeft alle categorieën terug; pak de eigen rij eruit.
    const row = verlofSettings?.find(
      (s) =>
        s.category === SYSTEM_SETTING_KEYS.verlof.category &&
        s.key === SYSTEM_SETTING_KEYS.verlof.vakantiedagenPerJaar
    );
    const value = Number(row?.value);
    if (Number.isFinite(value) && value > 0) setVakantiedagenPerJaar(value);
  }, [verlofSettings]);

  useEffect(() => {
    // De generieke lezer geeft alle categorieën terug; pak de eigen rij eruit.
    const row = verlofSettings?.find(
      (s) =>
        s.category === SYSTEM_SETTING_KEYS.verlof.category &&
        s.key === SYSTEM_SETTING_KEYS.verlof.pauzeVenster
    );
    const raw = typeof row?.value === 'string' ? row.value : undefined;
    const parsed = raw ? parsePauzeVenster(raw) : null;
    // Waarde-vergelijking i.p.v. de nieuwe object-referentie altijd
    // aan te nemen: `parsePauzeVenster` maakt elke keer een nieuw object,
    // dus zonder deze guard verandert de state-referentie bij elke render
    // waarin de lezer een nieuwe (inhoudelijk gelijke) array teruggeeft —
    // dat re-rendert de component eindeloos.
    if (parsed) {
      setPauzeVenster((prev) =>
        prev.start === parsed.start && prev.end === parsed.end ? prev : parsed
      );
    }
  }, [verlofSettings]);

  useEffect(() => {
    // De generieke lezer geeft alle categorieën terug; pak de eigen rij eruit.
    // Ontbreekt hij, dan blijft het veld leeg — geen standaarddatum (AC-2).
    const row = verlofSettings?.find(
      (s) =>
        s.category === SYSTEM_SETTING_KEYS.verlof.category &&
        s.key === SYSTEM_SETTING_KEYS.verlof.verlofsoortIngangsdatum
    );
    const raw = typeof row?.value === 'string' ? row.value : '';
    setVerlofsoortIngangsdatum((prev) => (prev === raw ? prev : raw));
  }, [verlofSettings]);

  const handleEvalChange = (key: keyof EvalSettings, value: number | boolean) => {
    setEvalForm((prev) => ({ ...prev, [key]: value }));
    setIsDirty(true);
  };

  const handleFeatureChange = (key: keyof Features, value: boolean) => {
    setFeaturesForm((prev) => ({ ...prev, [key]: value }));
    setIsDirty(true);
  };

  const handlePauzeVensterChange = (field: 'start' | 'end', value: string) => {
    setPauzeVenster((prev) => ({ ...prev, [field]: value }));
    setIsDirty(true);
  };

  // Story 1.1, AC-10/AC-11: dezelfde toets als het schrijfpad
  // (`pauzeVensterSchema`), de fout staat onder het veld waar hij bij hoort.
  const pauzeVensterValidation = pauzeVensterSchema.safeParse(
    serializePauzeVenster(pauzeVenster)
  );
  const pauzeVensterError =
    !pauzeVenster.start || !pauzeVenster.end
      ? 'Vul een begin- en eindtijd in.'
      : !pauzeVensterValidation.success
        ? pauzeVensterValidation.error.errors[0]?.message
        : undefined;

  const handleSave = async () => {
    if (pauzeVensterError) return;

    const settings: UpdateSystemSettingsRequest = {
      settings: [
        // Evaluation settings
        { category: 'evaluation', key: 'task_weight', value: evalForm.task_weight },
        { category: 'evaluation', key: 'vak_competency_weight', value: evalForm.vak_competency_weight },
        { category: 'evaluation', key: 'basis_competency_weight', value: evalForm.basis_competency_weight },
        { category: 'evaluation', key: 'score_scale_min', value: evalForm.score_scale_min },
        { category: 'evaluation', key: 'score_scale_max', value: evalForm.score_scale_max },
        { category: 'evaluation', key: 'auto_save_interval', value: evalForm.auto_save_interval },
        { category: 'evaluation', key: 'require_self_evaluation', value: evalForm.require_self_evaluation },
        // Feature flags
        { category: 'features', key: 'self_evaluation', value: featuresForm.self_evaluation },
        { category: 'features', key: 'conversation_mode', value: featuresForm.conversation_mode },
        { category: 'features', key: 'pdf_export', value: featuresForm.pdf_export },
        { category: 'features', key: 'salary_display', value: featuresForm.salary_display },
        // Verlof
        {
          category: SYSTEM_SETTING_KEYS.verlof.category,
          key: SYSTEM_SETTING_KEYS.verlof.vakantiedagenPerJaar,
          value: vakantiedagenPerJaar,
        },
        {
          category: SYSTEM_SETTING_KEYS.verlof.category,
          key: SYSTEM_SETTING_KEYS.verlof.pauzeVenster,
          value: serializePauzeVenster(pauzeVenster),
        },
        // Story 5.4, AC-10. Een lege waarde gaat gewoon mee: hem leegmaken is
        // hoe een beheerder het werk weer uitzet, en de lezer leest leeg als
        // "de nieuwe regels gelden niet".
        {
          category: SYSTEM_SETTING_KEYS.verlof.category,
          key: SYSTEM_SETTING_KEYS.verlof.verlofsoortIngangsdatum,
          value: verlofsoortIngangsdatum,
        },
      ],
    };

    await updateSettings.mutateAsync(settings);
    setIsDirty(false);
  };

  const isLoading = evalLoading || featuresLoading || verlofLoading;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600">Laden...</span>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Evaluation Settings */}
      <section>
        <div className="mb-4">
          <h2 className="text-lg font-medium text-gray-900">Beoordelingsinstellingen</h2>
          <p className="text-sm text-gray-500">
            Configureer hoe beoordelingen worden berekend en weergegeven
          </p>
        </div>

        <div className="bg-gray-50 rounded-lg p-6 space-y-6">
          {/* Weight Distribution */}
          <div>
            <h3 className="text-sm font-medium text-gray-900 mb-3">Gewichtsverdeling (100 punten)</h3>
            <p className="text-xs text-gray-500 mb-4">
              De totaalscore wordt berekend als de som van drie componenten. De punten per item worden dynamisch berekend op basis van het aantal items per medewerker.
            </p>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label htmlFor="task_weight" className="block text-sm font-medium text-gray-700">
                  Taken (%)
                </label>
                <input
                  type="number"
                  id="task_weight"
                  value={evalForm.task_weight}
                  onChange={(e) => handleEvalChange('task_weight', Number(e.target.value))}
                  min={0}
                  max={100}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                />
                <p className="mt-1 text-xs text-gray-400">Toegewezen aan medewerker</p>
              </div>
              <div>
                <label htmlFor="vak_competency_weight" className="block text-sm font-medium text-gray-700">
                  Vakcompetenties (%)
                </label>
                <input
                  type="number"
                  id="vak_competency_weight"
                  value={evalForm.vak_competency_weight}
                  onChange={(e) => handleEvalChange('vak_competency_weight', Number(e.target.value))}
                  min={0}
                  max={100}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                />
                <p className="mt-1 text-xs text-gray-400">Gerelateerd aan taken</p>
              </div>
              <div>
                <label htmlFor="basis_competency_weight" className="block text-sm font-medium text-gray-700">
                  Basiscompetenties (%)
                </label>
                <input
                  type="number"
                  id="basis_competency_weight"
                  value={evalForm.basis_competency_weight}
                  onChange={(e) => handleEvalChange('basis_competency_weight', Number(e.target.value))}
                  min={0}
                  max={100}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                />
                <p className="mt-1 text-xs text-gray-400">Toegewezen aan medewerker</p>
              </div>
            </div>
            {evalForm.task_weight + evalForm.vak_competency_weight + evalForm.basis_competency_weight !== 100 && (
              <p className="mt-2 text-sm text-yellow-600">
                Let op: De totale weging is {evalForm.task_weight + evalForm.vak_competency_weight + evalForm.basis_competency_weight} (moet 100 zijn)
              </p>
            )}
          </div>

          {/* Score Scale */}
          <div>
            <h3 className="text-sm font-medium text-gray-900 mb-3">Scoreschaal</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="score_scale_min" className="block text-sm font-medium text-gray-700">
                  Minimum score
                </label>
                <input
                  type="number"
                  id="score_scale_min"
                  value={evalForm.score_scale_min}
                  onChange={(e) => handleEvalChange('score_scale_min', Number(e.target.value))}
                  min={0}
                  max={10}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                />
              </div>
              <div>
                <label htmlFor="score_scale_max" className="block text-sm font-medium text-gray-700">
                  Maximum score
                </label>
                <input
                  type="number"
                  id="score_scale_max"
                  value={evalForm.score_scale_max}
                  onChange={(e) => handleEvalChange('score_scale_max', Number(e.target.value))}
                  min={1}
                  max={10}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                />
              </div>
            </div>
          </div>

          {/* Other Settings */}
          <div>
            <h3 className="text-sm font-medium text-gray-900 mb-3">Overige instellingen</h3>
            <div className="space-y-4">
              <div>
                <label htmlFor="auto_save_interval" className="block text-sm font-medium text-gray-700">
                  Automatisch opslaan interval (seconden)
                </label>
                <input
                  type="number"
                  id="auto_save_interval"
                  value={evalForm.auto_save_interval}
                  onChange={(e) => handleEvalChange('auto_save_interval', Number(e.target.value))}
                  min={10}
                  max={300}
                  step={10}
                  className="mt-1 block w-full max-w-xs rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                />
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="require_self_evaluation"
                  checked={evalForm.require_self_evaluation}
                  onChange={(e) => handleEvalChange('require_self_evaluation', e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <label htmlFor="require_self_evaluation" className="ml-2 text-sm text-gray-700">
                  Zelfevaluatie verplicht voor manager beoordeling
                </label>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Verlof */}
      <section>
        <div className="mb-4">
          <h2 className="text-lg font-medium text-gray-900">Verlof</h2>
          <p className="text-sm text-gray-500">
            De basis voor het getal "Nog te plannen dit jaar" dat medewerkers zien
          </p>
        </div>
        <div className="bg-white shadow rounded-lg p-6">
          <div className="max-w-md">
            <label htmlFor="vakantiedagen_per_jaar" className="block text-sm font-medium text-gray-700">
              Vakantiedagen per jaar (voltijds)
            </label>
            <input
              id="vakantiedagen_per_jaar"
              type="number"
              min={0}
              max={60}
              step={1}
              value={vakantiedagenPerJaar}
              onChange={(e) => {
                setVakantiedagenPerJaar(Number(e.target.value));
                setIsDirty(true);
              }}
              className="mt-1 block w-32 rounded-md border-[#E2E8F0] shadow-sm focus:border-[#54949E] focus:ring-[#54949E] sm:text-sm"
            />
            <p className="mt-2 text-sm text-gray-500">
              Bij een 5-daagse werkweek. Parttimers krijgen dit naar verhouding van hun
              contracturen. Het wettelijk minimum (4× de weekuren) staat vast; verhoog je dit
              getal, dan groeit alleen het bovenwettelijke deel.
            </p>
            <p className="mt-1 text-sm text-gray-500">
              Deze instelling geldt voor de hele organisatie — er is geen uitzondering per
              medewerker. Wie een afwijkend verlofrecht heeft, ziet daarom een jaarruimte die
              niet klopt; het scherm noemt de rekenbasis erbij zodat dat opvalt.
            </p>
          </div>

          <div className="max-w-md mt-8 pt-6 border-t border-gray-200">
            <h3 className="text-sm font-medium text-gray-900 mb-1">Vaste pauze</h3>
            <p className="text-sm text-gray-500 mb-3">
              Deze pauze telt niet mee als verlof. Hij geldt voor iedereen.
            </p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="pauze_venster_van" className="block text-sm font-medium text-gray-700">
                  Van
                </label>
                <input
                  id="pauze_venster_van"
                  type="time"
                  value={pauzeVenster.start}
                  onChange={(e) => handlePauzeVensterChange('start', e.target.value)}
                  aria-describedby={pauzeVensterError ? 'pauze_venster_error' : undefined}
                  aria-invalid={pauzeVensterError ? true : undefined}
                  className="mt-1 block w-full rounded-md border-[#E2E8F0] shadow-sm focus:border-[#54949E] focus:ring-[#54949E] sm:text-sm"
                />
              </div>
              <div>
                <label htmlFor="pauze_venster_tot" className="block text-sm font-medium text-gray-700">
                  Tot
                </label>
                <input
                  id="pauze_venster_tot"
                  type="time"
                  value={pauzeVenster.end}
                  onChange={(e) => handlePauzeVensterChange('end', e.target.value)}
                  aria-describedby={pauzeVensterError ? 'pauze_venster_error' : undefined}
                  aria-invalid={pauzeVensterError ? true : undefined}
                  className="mt-1 block w-full rounded-md border-[#E2E8F0] shadow-sm focus:border-[#54949E] focus:ring-[#54949E] sm:text-sm"
                />
              </div>
            </div>
            {pauzeVensterError && (
              <p id="pauze_venster_error" className="mt-2 text-sm text-[#D64545]">
                {pauzeVensterError}
              </p>
            )}
          </div>

          {/* Story 5.4, AC-10 — de schakelaar van de verlofsoorten. Naam en
              uitleg komen woord voor woord uit §10.7 (KE-7) en zijn hier niet
              geherformuleerd.

              KE-3: een echt datumveld met een gekoppeld label, dus met Tab
              bereikbaar, en de uitleg staat als tekst naast het veld — niet
              alleen in een tooltip.

              AC-11: wordt de datum midden in een maand gezet, dan telt die
              eerste maand gedeeltelijk mee. Dat is bekend en aanvaard, en er
              komt hier bewust géén melding over: geen sterretje, geen
              voetnoot, geen uitzonderingszin. */}
          <div className="max-w-md mt-8 pt-6 border-t border-gray-200">
            <label
              htmlFor="verlofsoort_ingangsdatum"
              className="block text-sm font-medium text-gray-700"
            >
              Vanaf wanneer gelden de nieuwe verlofsoorten
            </label>
            <input
              id="verlofsoort_ingangsdatum"
              type="date"
              value={verlofsoortIngangsdatum}
              onChange={(e) => {
                setVerlofsoortIngangsdatum(e.target.value);
                setIsDirty(true);
              }}
              aria-describedby="verlofsoort_ingangsdatum_uitleg"
              className="mt-1 block w-48 rounded-md border-[#E2E8F0] shadow-sm focus:border-[#54949E] focus:ring-[#54949E] sm:text-sm"
            />
            <p id="verlofsoort_ingangsdatum_uitleg" className="mt-2 text-sm text-gray-500">
              Aanvragen met een startdatum vóór deze dag krijgen geen soort en tellen niet mee.
            </p>
          </div>
        </div>
      </section>

      {/* Feature Flags */}
      <section>
        <div className="mb-4">
          <h2 className="text-lg font-medium text-gray-900">Functionaliteiten</h2>
          <p className="text-sm text-gray-500">
            Schakel systeemfunctionaliteiten in of uit
          </p>
        </div>

        <div className="bg-gray-50 rounded-lg p-6">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-900">Zelfevaluatie</label>
                <p className="text-sm text-gray-500">
                  Medewerkers kunnen zichzelf beoordelen voordat de manager beoordeelt
                </p>
              </div>
              <button
                type="button"
                onClick={() => handleFeatureChange('self_evaluation', !featuresForm.self_evaluation)}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                  featuresForm.self_evaluation ? 'bg-blue-600' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                    featuresForm.self_evaluation ? 'translate-x-5' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>

            <div className="flex items-center justify-between pt-4 border-t border-gray-200">
              <div>
                <label className="text-sm font-medium text-gray-900">Gespreksmodus</label>
                <p className="text-sm text-gray-500">
                  Side-by-side weergave voor beoordelingsgesprekken
                </p>
              </div>
              <button
                type="button"
                onClick={() => handleFeatureChange('conversation_mode', !featuresForm.conversation_mode)}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                  featuresForm.conversation_mode ? 'bg-blue-600' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                    featuresForm.conversation_mode ? 'translate-x-5' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>

            <div className="flex items-center justify-between pt-4 border-t border-gray-200">
              <div>
                <label className="text-sm font-medium text-gray-900">PDF Export</label>
                <p className="text-sm text-gray-500">
                  Beoordelingen kunnen als PDF worden gedownload
                </p>
              </div>
              <button
                type="button"
                onClick={() => handleFeatureChange('pdf_export', !featuresForm.pdf_export)}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                  featuresForm.pdf_export ? 'bg-blue-600' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                    featuresForm.pdf_export ? 'translate-x-5' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>

            <div className="flex items-center justify-between pt-4 border-t border-gray-200">
              <div>
                <label className="text-sm font-medium text-gray-900">Salarisinformatie tonen</label>
                <p className="text-sm text-gray-500">
                  Toon salarisschalen en indicatieve bedragen aan medewerkers
                </p>
              </div>
              <button
                type="button"
                onClick={() => handleFeatureChange('salary_display', !featuresForm.salary_display)}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                  featuresForm.salary_display ? 'bg-blue-600' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                    featuresForm.salary_display ? 'translate-x-5' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Save Button */}
      {isDirty && (
        <div className="flex justify-end pt-4 border-t border-gray-200">
          <button
            onClick={handleSave}
            disabled={updateSettings.isPending || Boolean(pauzeVensterError)}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
          >
            {updateSettings.isPending ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Opslaan...
              </>
            ) : (
              'Wijzigingen opslaan'
            )}
          </button>
        </div>
      )}
    </div>
  );
}
